import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KeyRound, Loader2, Mail, ArrowLeft, Eye, EyeOff } from "lucide-react";

/**
 * Self-serve password reset — accessible from EVERY login page and by EVERY role.
 * Two-step flow: (1) request code by email → (2) enter code + new password.
 * On success the caller can optionally pre-fill the login form.
 */
export default function ForgotPasswordDialog({ open, onClose, onResetComplete, initialEmail = "" }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(initialEmail);
  const [sending, setSending] = useState(false);
  const [code, setCode] = useState("");
  const [np, setNp] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Whenever the dialog opens, sync email from the parent (may have been typed after mount)
  useEffect(() => {
    if (open) {
      setStep(1);
      setEmail(initialEmail || "");
      setCode(""); setNp(""); setConfirm("");
    }
  }, [open, initialEmail]);

  const reset = () => { setStep(1); setEmail(initialEmail || ""); setCode(""); setNp(""); setConfirm(""); };

  const request = async () => {
    if (!email) { toast.error("Enter your email."); return; }
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If your email is registered, a reset code has been sent.");
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reset code");
    } finally { setSending(false); }
  };

  const submit = async () => {
    if (!code || code.length < 4) { toast.error("Enter the 6-character reset code from your email."); return; }
    if (!np || np.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (np !== confirm) { toast.error("Passwords don't match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { email, code: code.trim().toUpperCase(), new_password: np });
      toast.success("Password reset. Sign in with your new password.");
      onResetComplete?.({ email, password: np });
      reset();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-orange-500" /> Reset your password
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Enter your work email. We'll send a one-time reset code. Works for every role, every tenant."
              : "Enter the 6-character code from your email + choose a new password."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Work email</Label>
              <div className="relative">
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="fp-email-input" autoComplete="email" />
                <Mail className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
              <div className="text-[10px] text-muted-foreground">If your account exists we'll email you a code within a minute. If you don't receive it, ask your Super Admin to check the Welcome Messages inbox.</div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Email</Label>
              <Input value={email} disabled className="opacity-70" data-testid="fp-email-locked" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Reset code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={8} placeholder="e.g. D35HJP" className="uppercase font-mono tracking-widest text-center text-lg" data-testid="fp-code-input" />
              <div className="text-[10px] text-muted-foreground">Expires in 30 minutes. Case-insensitive.</div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">New password</Label>
              <div className="relative">
                <Input type={showPw ? "text" : "password"} value={np} onChange={(e) => setNp(e.target.value)} autoComplete="new-password" data-testid="fp-newpw-input" />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-[10px] text-muted-foreground">Min 8 chars.</div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Confirm new password</Label>
              <Input type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="fp-confirm-input" />
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {step === 2 && (
            <Button variant="ghost" onClick={() => setStep(1)} data-testid="fp-back">
              <ArrowLeft className="w-3 h-3 mr-1" /> Back
            </Button>
          )}
          <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="fp-cancel">Cancel</Button>
          {step === 1
            ? <Button onClick={request} disabled={sending} data-testid="fp-send">
                {sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending</> : "Send reset code"}
              </Button>
            : <Button onClick={submit} disabled={saving} data-testid="fp-submit">
                {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Resetting</> : "Reset password"}
              </Button>
          }
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
